"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { api } from "@/lib/api";
import { setCustomerSession } from "@/lib/auth";
import { motion, AnimatePresence } from "framer-motion";
import { 
  User, 
  Mail, 
  ArrowLeft, 
  ChevronRight, 
  ShieldCheck,
  Phone as PhoneIcon,
  Zap,
  MapPin,
  Lock,
  Eye,
  EyeOff,
  Clock
} from "lucide-react";
import { PhoneInput } from "@/components/ui/PhoneInput";
import { PasswordRequirements } from "@/components/ui/PasswordRequirements";
import { isStrongPassword, PASSWORD_REQUIREMENTS } from "@/lib/password";

export default function RegisterPage() {
  const router = useRouter();
  const [form, setForm] = useState({ firstName: "", middleName: "", lastName: "", email: "", phone: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  function update(f: string, v: string) { setForm(p => ({ ...p, [f]: v })); setError(""); }

  async function handleSubmit() {
    if (!form.firstName || !form.lastName || !form.email || !form.password) { setError("First name, last name, email and password are required"); return; }
    if (!isStrongPassword(form.password)) { setError(PASSWORD_REQUIREMENTS); return; }
    if (form.password !== form.confirm) { setError("Passwords don't match"); return; }
    setLoading(true);
    try {
      const fullName = `${form.firstName} ${form.middleName ? form.middleName + " " : ""}${form.lastName}`.trim();
      const result = await api.register({ name: fullName, email: form.email, password: form.password, phone: form.phone || undefined }) as any;
      setCustomerSession(result.token, result.user);
      const searchParams = new URLSearchParams(window.location.search);
      const redirect = searchParams.get("redirect");
      router.push(redirect || "/dashboard");
    } catch (e: any) {
      setError(e.message || "Registration failed");
    } finally { setLoading(false); }
  }

  const reasons = [
    {
      icon: <Zap size={24} className="text-amber-500" />,
      title: "Instant AI Estimate",
      desc: "Get an upfront price range in seconds, then compare real bids."
    },
    {
      icon: <ShieldCheck size={24} className="text-emerald-500" />,
      title: "Local Trades Network",
      desc: "Browse contractors in your area and check their details before you book."
    },
    {
      icon: <Clock size={24} className="text-blue-500" />,
      title: "24/7 Digital Maintenance Log",
      desc: "Keep a complete history of all your property repairs and costs."
    }
  ];

  return (
    <main className="min-h-screen flex flex-col lg:flex-row font-sans">
      {/* Left Side: Branding & Reasons */}
      <div className="hidden lg:flex lg:w-1/2 bg-white pt-32 px-16 pb-16 flex-col justify-between relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full opacity-60 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[60%] h-[60%] bg-emerald-100 rounded-full blur-[120px]" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[60%] h-[60%] bg-blue-100 rounded-full blur-[120px]" />
        </div>

        <div className="relative z-10">
          <Link href="/" className="brand-logo-shell group">
            <img
              src="/lastest-logo-fixer.png"
              alt="FixerLocal"
              className="brand-logo h-12 select-none transition-transform group-hover:scale-[1.03]"
            />
          </Link>
        </div>

        <div className="relative z-10 max-w-lg">
          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl font-bold text-slate-950 leading-tight mb-8"
          >
            Start your property <br />
            <span className="text-emerald-500">care journey.</span>
          </motion.h1>
          
          <div className="space-y-10 mt-16">
            {reasons.map((reason, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 + 0.3 }}
                className="flex gap-6 items-start"
              >
                <div className="h-12 w-12 rounded-2xl bg-emerald-50 border border-emerald-100 flex items-center justify-center shrink-0">
                  {reason.icon}
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-950 mb-1">{reason.title}</h3>
                  <p className="text-slate-600 font-medium leading-relaxed">{reason.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <div className="relative z-10 text-slate-500 text-sm font-bold">
          © 2026 Fixer Technologies. All rights reserved.
        </div>
      </div>

      {/* Right Side: Register Form */}
      <div className="flex-1 flex flex-col items-center justify-center px-6 pt-32 pb-12 bg-mesh lg:bg-white">
        <div className="w-full max-w-[540px]">
          {/* Mobile Logo */}
          <div className="lg:hidden flex justify-center mb-12">
             <img
               src="/lastest-logo-fixer.png"
               alt="FixerLocal"
               className="h-14 w-auto select-none"
             />
          </div>

          <div className="mb-10 text-center lg:text-left">
            <h2 className="text-4xl font-bold text-slate-950 tracking-tight mb-3">Customer Registration</h2>
            <p className="text-slate-500 font-medium text-lg">Join Fixer for premium property maintenance and repair services.</p>
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1">First Name</label>
                <div className="relative group">
                  <User className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" size={20} />
                  <input 
                    type="text" 
                    value={form.firstName} 
                    onChange={e => update("firstName", e.target.value)}
                    placeholder="John"
                    className="w-full h-16 rounded-2xl bg-slate-50 border border-slate-200 pl-14 pr-4 text-slate-950 font-medium outline-none focus:bg-white focus:border-emerald-600 focus:ring-4 focus:ring-emerald-50 transition-all" 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1">Last Name</label>
                <div className="relative group">
                  <User className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" size={20} />
                  <input 
                    type="text" 
                    value={form.lastName} 
                    onChange={e => update("lastName", e.target.value)}
                    placeholder="Smith"
                    className="w-full h-16 rounded-2xl bg-slate-50 border border-slate-200 pl-14 pr-4 text-slate-950 font-medium outline-none focus:bg-white focus:border-emerald-600 focus:ring-4 focus:ring-emerald-50 transition-all" 
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              {/* <label className="text-sm font-bold text-slate-700 ml-1">Middle Name (Optional)</label> */}
              {/* <div className="relative group">
                <User className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" size={20} />
                <input 
                  type="text" 
                  value={form.middleName} 
                  onChange={e => update("middleName", e.target.value)}
                  placeholder="Quincy"
                  className="w-full h-16 rounded-2xl bg-slate-50 border border-slate-200 pl-14 pr-4 text-slate-950 font-medium outline-none focus:bg-white focus:border-emerald-600 focus:ring-4 focus:ring-emerald-50 transition-all" 
                />
              </div> */}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1">Email Address</label>
                <div className="relative group">
                  <Mail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" size={20} />
                  <input 
                    type="email" 
                    value={form.email} 
                    onChange={e => update("email", e.target.value)}
                    placeholder="john@example.com"
                    className="w-full h-16 rounded-2xl bg-slate-50 border border-slate-200 pl-14 pr-4 text-slate-950 font-medium outline-none focus:bg-white focus:border-emerald-600 focus:ring-4 focus:ring-emerald-50 transition-all" 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1">Phone Number</label>
                <PhoneInput 
                  value={form.phone} 
                  onChange={val => update("phone", val)}
                  placeholder="Your mobile number"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1">Password</label>
                <div className="relative group">
                  <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" size={20} />
                  <input 
                    type={showPassword ? "text" : "password"} 
                    value={form.password} 
                    onChange={e => update("password", e.target.value)}
                    placeholder="Strong password"
                    className="w-full h-16 rounded-2xl bg-slate-50 border border-slate-200 pl-14 pr-4 text-slate-950 font-medium outline-none focus:bg-white focus:border-emerald-600 focus:ring-4 focus:ring-emerald-50 transition-all" 
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-700 ml-1">Confirm Password</label>
                <div className="relative group">
                  <Lock className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-emerald-600 transition-colors" size={20} />
                  <input 
                    type={showPassword ? "text" : "password"} 
                    value={form.confirm} 
                    onChange={e => update("confirm", e.target.value)}
                    placeholder="Repeat password"
                    className="w-full h-16 rounded-2xl bg-slate-50 border border-slate-200 pl-14 pr-11 text-slate-950 font-medium outline-none focus:bg-white focus:border-emerald-600 focus:ring-4 focus:ring-emerald-50 transition-all" 
                  />
                   <button onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            </div>
            <PasswordRequirements password={form.password} />

            <AnimatePresence>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-4 rounded-2xl bg-red-50 border border-red-100 text-red-700 text-sm font-bold flex items-center gap-3"
                >
                  <ShieldCheck size={16} fill="currentColor" />
                  {error}
                </motion.div>
              )}
            </AnimatePresence>

            <button 
              onClick={handleSubmit} 
              disabled={loading}
              className="w-full h-18 rounded-[1.25rem] bg-emerald-600 text-white font-bold text-lg hover:bg-emerald-700 disabled:opacity-50 transition-all shadow-xl shadow-emerald-100 flex items-center justify-center gap-3 active:scale-[0.98] mt-4 py-5"
            >
              {loading ? (
                <div className="h-6 w-6 border-3 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  Register
                  <ChevronRight size={20} />
                </>
              )}
            </button>
          </div>

          <div className="mt-10 pt-8 border-t border-slate-100 text-center">
            <p className="text-slate-500 font-medium">
              Already have an account?{" "}
              <Link href="/login" className="text-emerald-600 font-bold hover:underline underline-offset-4">Login here</Link>
            </p>
          </div>

          <div className="mt-10 flex justify-center items-center gap-6">
            <Link href="/contractor/onboarding" className="text-xs font-bold text-slate-400 uppercase tracking-widest hover:text-slate-600 transition-colors">Contractor Registration</Link>
          </div>
        </div>
      </div>
    </main>
  );
}
